import { z } from "zod";

/** Soorten e-mailmeldingen per gebouw. De eerste drie zijn "alles", de rest "belangrijk". */
export const NOTIFY_KINDS = [
  "new_issue",
  "updated",
  "resolved",
  "urgent",
  "high",
  "task_assigned",
  "comment_on_mine",
  "mention",
  "obligation",
] as const;
export type NotifyKind = (typeof NOTIFY_KINDS)[number];

export const NOTIFY_LABELS: Record<NotifyKind, { l: string; t: string }> = {
  new_issue: { l: "Nieuwe bevinding", t: "Elke nieuw vastgelegde bevinding" },
  updated: { l: "Update", t: "Wijziging, statuswissel, opmerking of foto" },
  resolved: { l: "Afgerond", t: "Bevinding opgelost of gesloten" },
  urgent: { l: "Spoed (P1)", t: "Nieuwe bevinding of opgewaardeerd naar spoed, ook bij direct gevaar" },
  high: { l: "Hoog (P2)", t: "Nieuwe bevinding of opgewaardeerd naar hoog" },
  task_assigned: { l: "Taak aan mij toegewezen", t: "Als jij een taak krijgt" },
  comment_on_mine: { l: "Opmerking op mijn bevinding", t: "Als iemand reageert op een bevinding die jij vastlegde" },
  mention: { l: "Genoemd in een logboek", t: "Als iemand je met @ noemt bij een bevinding of een melding in de Inbox" },
  obligation: {
    l: "Keuring binnenkort of verlopen",
    t: "Verplichting bij een asset; een verlopen wettelijke keuring gaat direct als spoed",
  },
};
export const NOTIFY_GROUPS: { l: string; kinds: NotifyKind[] }[] = [
  { l: "Alles in dit gebouw", kinds: ["new_issue", "updated", "resolved"] },
  { l: "Belangrijk", kinds: ["urgent", "high", "task_assigned", "comment_on_mine", "mention", "obligation"] },
];

export const notificationPrefsSchema = z.object(
  Object.fromEntries(NOTIFY_KINDS.map((k) => [k, z.boolean()])) as Record<NotifyKind, z.ZodBoolean>,
);
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

/** Standaard: alleen de belangrijke zaken. */
export const DEFAULT_NOTIFY_PREFS: NotificationPrefs = {
  new_issue: false,
  updated: false,
  resolved: false,
  urgent: true,
  high: true,
  task_assigned: true,
  comment_on_mine: true,
  mention: true,
  obligation: true,
};

/** Opgeslagen voorkeuren (mogelijk onvolledig) aangevuld met de standaard. */
export const withDefaultPrefs = (stored: unknown): NotificationPrefs => ({
  ...DEFAULT_NOTIFY_PREFS,
  ...notificationPrefsSchema
    .partial()
    .catch({})
    .parse(stored ?? {}),
});

export interface NotificationPrefsResponse {
  prefs: NotificationPrefs;
  mailEnabled: boolean;
}

// ---------- pushmeldingen (web push, issue #223) ----------

/** Soorten pushmeldingen; per gebruiker aan of uit (geldt voor al zijn apparaten). */
export const PUSH_KINDS = ["intake", "mention"] as const;
export type PushKind = (typeof PUSH_KINDS)[number];

export const PUSH_LABELS: Record<PushKind, { l: string; t: string }> = {
  intake: { l: "Nieuw in de Inbox", t: "Een nieuwe melding in de Inbox van een gebouw dat je beoordeelt" },
  mention: { l: "Genoemd met @", t: "Als iemand je noemt in een logboek" },
};

export const pushPrefsSchema = z.object(
  Object.fromEntries(PUSH_KINDS.map((k) => [k, z.boolean()])) as Record<PushKind, z.ZodBoolean>,
);
export type PushPrefs = z.infer<typeof pushPrefsSchema>;
export const DEFAULT_PUSH_PREFS: PushPrefs = { intake: true, mention: true };
export const withDefaultPushPrefs = (stored: unknown): PushPrefs => ({
  ...DEFAULT_PUSH_PREFS,
  ...pushPrefsSchema
    .partial()
    .catch({})
    .parse(stored ?? {}),
});

/** Abonnement van de browser (PushSubscription.toJSON()). */
export const pushSubscriptionSchema = z.object({
  endpoint: z.url().max(2000),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
  /** Korte naam van het apparaat, bv. "iPhone · Safari". */
  label: z.string().max(80).optional(),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

export interface PushDeviceDto {
  id: string;
  label: string | null;
  createdAt: string;
  /** Dit apparaat (zelfde endpoint als de vraag meestuurde). */
  current: boolean;
}

export interface PushSettingsResponse {
  /** Staat web push aan op de server (VAPID-sleutels ingesteld)? */
  enabled: boolean;
  publicKey: string | null;
  prefs: PushPrefs;
  devices: PushDeviceDto[];
}

/** Inhoud van een pushbericht (door de service worker getoond). */
export interface PushPayload {
  title: string;
  body: string;
  /** Pad in de app, inclusief `org=` zodat de juiste organisatie opent. */
  url: string;
  /** Zelfde tag vervangt een eerdere melding over hetzelfde. */
  tag?: string;
}
